import {
  Backdrop,
  Box,
  CircularProgress,
  InputAdornment,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as constants from "../../../../constants";
import ContainedButton from "../../../../shared/Button/CustomContainedButton";
import CustomTextField from "../../../../shared/CustomTextFeild";
import ReusableTable from "../../../../shared/ReusableTable/ReusableTable";
import { RootState } from "../../../../store/store";
import { formatCurrency } from "../../../core/Util";
import { default as basecss, default as basestyle } from "../../../CSS/Base.module.css";
import ClosePopUp from "../../../PopUp/CloseCreditLine";
import {
  getCreditLineDetails,
  getCreditLinePandIAmountbyCreditId,
  postCreditLinesMergertoloan,
  putCreditLineDetails,
} from "../../services/CreditLine/CreditLineServices";
import { StyledActAndDeActButton } from "../../StyledComponents/Button";
import {
  SectionTitle,
  StyledOneChildContainer,
  StyledParentContainer,
  StyledTwoChildContainer,
  SubSectionTitle,
} from "../../StyledComponents/Container";
import { StyledInputLabel } from "../../StyledComponents/InputLabel";
import { getClientDetailsByClientName } from "../../Utils/ClientSettingsTable";
import { formatDateTime } from "../../Utils/DataFormat";
import { notifyUser } from "../../Utils/notifyUser";
import { getTodayDate } from "../../services/Settings/SystemSettingsService";


const parseAvailableCredit = (creditStr: any) => {
  if (!creditStr) return null;

  // Remove $ and commas, then parse as float
  const value = parseFloat(creditStr.replace(/[$,]/g, ""));

  // Return formatted result
  return value === 0 ? "0.00" : value;
};

interface Column {
  id: string;
  label: string;
  minWidth: number;
  align?: "center" | "left" | "right";
}

type CloseCreditLinePayload = {
  creditId: number;
  principal: number;
  interest: number;
  totalAmount: number;
  creditLineDescription: string;
  loanAccount: {
    id: number;
    name: string;
    [key: string]: any;
  };
};

const CreditDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [todayDate, setTodayDate] = useState<string | null>(null);
  const [transactionDate, setTransactionDate] = useState<string>("");
  const userInfo = useSelector((state: RootState) => state.auth);
  const ClientDetails = getClientDetailsByClientName(userInfo?.clientVo?.name);
  let role: any = userInfo.roles?.roleName;
  const CreditId = location?.state?.creditlineId;
  const CreditLineName = location?.state?.name;

  const [formData, setFormData] = useState({
    type: "",
    projectNumber: "",
    projectTerm: "",
    paymentDueDate: "",
    interestRate: "",
    description: "",
    approvedCreditLine: 0,
    availableCredit: 0,
    balance: "",
    status: "",
    creditlineTransactionHistory: [],
    version: 0,
    name: "",
    paymentSourceAccountAvailableBalance: 0,
    amountPaid: null,
    source: null,
    fundId: 0,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [dataLoading, setDataLoading] = useState(true);
  const [openClosePopUp, setOpenClosePopUp] = useState(false);
  const [principal, setPrincipal] = useState(0.00);
  const [interest, setInterest] = useState(0.00);
  const [totalAmount, setTotalAmount] = useState(0.00);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    setFormData((prevState) => ({
      ...prevState,
      [field]: event.target.value,
    }));
  };

  const handleColseCreditLine = () => {
    setOpenClosePopUp(true);
  };
  const handlePopUpClose = () => {
    setOpenClosePopUp(false);
  };
  const handleSubmit = async (data: CloseCreditLinePayload) => {
    setDataLoading(true);
    setOpenClosePopUp(false);

    const postCreditLinesMerger = {
      id: 0,
      clientId: userInfo?.clientId,
      creditlineId: data.creditId,
      loanId: data.loanAccount.id,
      creditLineDescription: data.creditLineDescription,
      principalAmount: data.principal,
      accruedInterestCreditLine: data.interest
    };

    try {
      const response = await postCreditLinesMergertoloan(postCreditLinesMerger);
      if (response && response?.status === constants.STATUS_CODES.SUCCESS) {
        notifyUser(
          response.data?.message,
          "success",
          translate("creditLines")
        );
      } else {
        notifyUser(
          response.response.data.message,
          "error",
          translate("creditLines")
        );
      }
    } catch (error: any) {
      notifyUser(
        error?.response?.data?.message || translate("anUnexpectedError"),
        "error",
        translate("creditLines")
      );
    } finally {
      setDataLoading(false);
      navigate("/creditLineSummaryListing");
    }
  };
  const handleStatusClick = async () => {
    try {
      setDataLoading(true);
      const creditlineVo = {
        status:
          formData.status === constants.IS_Status_ACTIVE
            ? constants.IS_Status_INACTIVE
            : constants.IS_Status_ACTIVE,
        description: formData.description,
      };
      const response = await putCreditLineDetails(CreditId, creditlineVo);
      if (response.status === constants.STATUS_CODES.SUCCESS) {
        notifyUser(response.data.message, "success", translate("creditLines"));
        getCreditDetailsByID();
      } else {
        notifyUser(
          response.response.data.message,
          "error",
          translate("creditLines")
        );
      }
    } catch (error) {
      console.error("Error saving AutoDebit details:", error);
    } finally {
      setDataLoading(false);
    }
  };

  const handleDescriptionClick = async () => {
    try {
      setDataLoading(true);
      const creditlineVo = {
        status: formData.status,
        description: formData.description,
      };
      const response = await putCreditLineDetails(CreditId, creditlineVo);
      if (response.status === constants.STATUS_CODES.SUCCESS) {
        notifyUser(response.data.message, "success", translate("creditLines"));
        getCreditDetailsByID();
      } else {
        notifyUser(
          response.response.data.message,
          "error",
          translate("creditLines")
        );
      }
    } catch (error) {
      console.error("Error saving AutoDebit details:", error);
    } finally {
      setDataLoading(false);
    }
  };

  async function getCreditDetailsByID() {
    setDataLoading(true);
    try {
      const responseList = await getCreditLineDetails(
        CreditId,
        userInfo.clientId
      );

      if (responseList.status === constants.STATUS_CODES.SUCCESS) {
        const data = responseList.data.data[0];
        setFormData((prevData) => ({
          ...prevData,
          type: data.type,
          projectNumber: data.projectNumber,
          projectTerm: data.projectDuration,
          paymentDueDate: data.paymentDueDate ? data.paymentDueDate : "N/A",
          interestRate: data.interestRate,
          description: data.description,
          approvedCreditLine:
            data.creditlineTransactionHistory[0].availableCredit,
          availableCredit:
            data.status === constants.IS_Status_ACTIVE
              ? data.availableCredit
              : 0.0,
          status: data.status,
          creditlineTransactionHistory: data.creditlineTransactionHistory,
          version: data.version,
          name: data.name,
          paymentSourceAccountAvailableBalance:
            data.paymentSourceAccountAvailableBalance,
          amountPaid: data.amountPaid,
          source: data.source,
          fundId: data.fundId,
        }));
      } else {
        notifyUser(
          responseList.response.data.message,
          "error",
          translate("creditLines")
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setDataLoading(false);
    }
  }
  async function fetchDateFromDB() {
    try {
      const response: any = await getTodayDate(constants.ATTRIBUTE_NAME.SETTING_TODAY_SYSDATE_TEST_ENV_ONLY, userInfo.clientId);
      let dateStr: any = response.data.data;
      setTodayDate(dateStr);
    } catch (error) {
      console.error("Error fetching date:", error);
    }
  }
  const getLocalToday = () => {
    if (!todayDate) return "";
    return todayDate.split("T")[0];
  };
  useEffect(() => {
    fetchDateFromDB();
  }, []);
  useEffect(() => {
    if (todayDate) {
      setTransactionDate(getLocalToday());
    }
  }, [todayDate]);
  useEffect(() => {
    if (openClosePopUp) {
      getCreditLineAmountDetailsbyCreditId(CreditId, userInfo?.clientId, transactionDate);
    }
  }, [openClosePopUp, CreditId, transactionDate]);

  async function getCreditLineAmountDetailsbyCreditId(creditId: number, clientId: number, transactionDate: string) {
    try {
      const response = await getCreditLinePandIAmountbyCreditId(creditId, clientId, transactionDate);
      if (response.status === constants.STATUS_CODES.SUCCESS) {
        const details = response.data.data;
        const interestVal = Number(details?.accruedInterest) || 0;
        const principalVal = Number(details?.outstandingBalance) || 0;
        const interest = interestVal > 0 ? formatCurrency(interestVal) : "";
        const principal = principalVal > 0 ? formatCurrency(principalVal) : "";
        setInterest(interest);
        setPrincipal(principal);
        let total = 0;
        if (interestVal > 0 && principalVal > 0) {
          total = interestVal + principalVal;
        } else if (interestVal > 0) {
          total = interestVal;
        } else if (principalVal > 0) {
          total = principalVal;
        } else {
          total = 0;
        }
        if (total > 0) {
          setTotalAmount(total);
        } else {
          setTotalAmount(total);
          notifyUser("No Credit Line amount left to merge with the loan.", "error", translate("creditLines"));
        }
      } else {
        setOpenClosePopUp(false);
        notifyUser(response?.response?.data?.message, 'error', translate("creditLines"));
      }
    } catch (error: any) {
      setOpenClosePopUp(false);
      notifyUser(error.response?.data?.message || translate("anUnexpectedError"), 'error', translate("creditLines"));
    }
  }

  useEffect(() => {
    getCreditDetailsByID();
  }, [page, rowsPerPage]);
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = formData.creditlineTransactionHistory
    .sort((a: any, b: any) => b.id - a.id)
    .slice(startIndex, endIndex)
    .map((item: any) => ({
      ...item,
      postingDate: formatDateTime(item?.postingDate) || "-",
      paymentAmount: formatCurrency(item?.paymentAmount || "-"),
      interest: formatCurrency(item?.interest || "-"),
    }));

  const Columns: Column[] = [
    {
      id: "postingDate",
      label: translate("date"),
      minWidth: 150,
      align: "center",
    },
    {
      id: "description",
      label: translate("description"),
      minWidth: 300,
      align: "left",
    },
    {
      id: "transactionType",
      label: translate("type"),
      minWidth: 50,
      align: "left",
    },
    {
      id: "paymentAmount",
      label: translate("amount"),
      minWidth: 50,
      align: "right",
    },
    {
      id: "interest",
      label: translate("interest"),
      minWidth: 50,
      align: "right",
    },
  ];
  return (
    <StyledParentContainer>
      <StyledOneChildContainer>
        <StyledTwoChildContainer sx={{ border: "none" }}>
          <SectionTitle>
            {translate("creditLine")}: {CreditLineName}
          </SectionTitle>
          <StyledTwoChildContainer>
            <Box sx={{ width: "100%", padding: "40px" }}>
              <Grid container spacing={2}>
                <Grid
                  size={{ xs: 12, sm: 6, lg: 4 }}
                  container
                  spacing={2}
                  direction={"column"}
                >
                  <Grid size={12}>
                    <CustomTextField
                      labelText={translate("creditLineType")}
                      value={formData.type}
                      readOnly={true}
                    />
                  </Grid>
                  {ClientDetails?.newCreditLineProjectNumber === constants.IS_ACTIVE_YES && (<Grid size={12}>
                    <CustomTextField
                      labelText={translate("projectNumber")}
                      value={formData.projectNumber}
                      readOnly={true}
                    />
                  </Grid>)}
                  <Grid size={12}>
                    <CustomTextField
                      labelText={translate("projectTerm")}
                      value={formData.projectTerm}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {translate("month")}
                          </InputAdornment>
                        ),
                      }}
                      readOnly={true}
                    />
                  </Grid>
                  <Grid size={12}>
                    <CustomTextField
                      labelText={translate("paymentDueDate")}
                      value={formData.paymentDueDate}
                      readOnly={true}
                    />
                  </Grid>
                  <Grid size={12}>
                    <NumericFormat
                      customInput={CustomTextField}
                      id="interest-rate"
                      decimalScale={2}
                      readOnly
                      labelText={translate("interestRate(APR)")}
                      allowNegative={false}
                      value={formData.interestRate}
                      suffix="%"
                    />
                  </Grid>
                  <Grid size={12}>
                    <CustomTextField
                      labelText={translate("description")}
                      rows={4}
                      multiline
                      value={formData.description}
                      onChange={(e) => handleInputChange(e, "description")}
                      onBlur={handleDescriptionClick}
                      inputProps={{ maxLength: "250" }}
                      readOnly={
                        [
                          constants.ROLES_BUDGET_ANALYST,
                          constants.ROLES_PARTICIPANT_USER,
                          constants.ROLES_READ_ONLY_USER,
                          constants.ROLES_PROJECT_APPROVER,
                          constants.ROLES_READ_ONLY_ALL_LOCATIONS,
                        ].includes(role) && true
                      }
                    />
                  </Grid>
                </Grid>
                <Grid
                  size={{ xs: 12, sm: 6, lg: 4 }}
                  container
                  spacing={2}
                  direction={"column"}
                >
                  <Grid size={12}>
                    <NumericFormat
                      customInput={CustomTextField}
                      id="approved-credit-line"
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      labelText={translate("approvedCreditLine")}
                      value={formData.approvedCreditLine || 0}
                      allowNegative={false}
                      readOnly
                      prefix="$"
                    />
                  </Grid>
                  <Grid size={12}>
                    <NumericFormat
                      customInput={CustomTextField}
                      id="available-credit"
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      labelText={translate("availableCredit")}
                      value={
                        formData.status === "Active"
                          ? formData.availableCredit || 0
                          : 0.0
                      }
                      allowNegative={false}
                      readOnly
                      prefix="$"
                    />
                  </Grid>
                  <Grid size={12}>
                    <CustomTextField
                      labelText={translate("balance")}
                      value={parseAvailableCredit(
                        location?.state?.outstandingBalance
                      )}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }}
                      readOnly={true}
                    />
                  </Grid>
                  {ClientDetails?.creditLineActiveInactive === constants.IS_ACTIVE_YES && <Grid size={{ xs: 12, sm: 6 }}>
                    <StyledInputLabel shrink>
                      {translate("creditLineStatus")}
                    </StyledInputLabel>
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Typography
                        color={
                          formData.status === constants.IS_Status_ACTIVE
                            ? translate("success")
                            : translate("error")
                        }
                        sx={{ mt: 1 }}
                        className={basecss.activeAndInactive}
                      >
                        {formData.status === constants.IS_Status_ACTIVE
                          ? translate("active")
                          : translate("inactive")}
                      </Typography>
                      {![
                        constants.ROLES_BUDGET_ANALYST,
                        constants.ROLES_PARTICIPANT_USER,
                        constants.ROLES_READ_ONLY_USER,
                        constants.ROLES_PROJECT_APPROVER,
                        constants.ROLES_READ_ONLY_ALL_LOCATIONS,
                        constants.ROLES_FINANCIAL_ANALYST
                      ].includes(role) && (
                          <StyledActAndDeActButton
                            variant="contained"
                            color={
                              formData.status === constants.IS_Status_ACTIVE
                                ? "error"
                                : "success"
                            }
                            onClick={handleStatusClick}
                          >
                            {formData.status === constants.IS_Status_ACTIVE
                              ? translate("deActivate")
                              : translate("activate")}
                          </StyledActAndDeActButton>
                        )}
                    </Box>
                  </Grid>}
                  {ClientDetails?.creditLineMergingToLoan === constants.IS_ACTIVE_YES && formData.status === constants.IS_Status_ACTIVE && <Grid size={{ xs: 12, sm: 6 }}  >
                    <StyledInputLabel shrink>
                      {translate("mergingCreditLine")}
                    </StyledInputLabel>
                    <Box sx={{ display: "flex", gap: 2 }}>

                      {![
                        constants.ROLES_BUDGET_ANALYST,
                        constants.ROLES_PARTICIPANT_USER,
                        constants.ROLES_READ_ONLY_USER,
                        constants.ROLES_PROJECT_APPROVER,
                        constants.ROLES_READ_ONLY_ALL_LOCATIONS,
                      ].includes(role) && (
                          <StyledActAndDeActButton
                            variant="contained"
                            color={"error"}
                            onClick={handleColseCreditLine}
                          >   {translate("mergingCreditLine")}
                          </StyledActAndDeActButton>
                        )}
                    </Box>
                  </Grid>}

                </Grid>
              </Grid>
            </Box>
          </StyledTwoChildContainer>
          <SubSectionTitle>
            {translate("creditLinePaymentHistory")}{" "}
          </SubSectionTitle>
          <ReusableTable
            columns={Columns}
            rows={paginatedData}
            count={formData.creditlineTransactionHistory.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            loading={dataLoading}
          />
          <div className={basestyle.buttoncontainer}>
            <ContainedButton
              onClick={() => navigate("/creditLineSummaryListing")}
            >
              {translate("back")}
            </ContainedButton>
          </div>
          {CreditId && totalAmount > 0 && <ClosePopUp
            open={openClosePopUp}
            popupTitle={translate("mergingCreditLine")}
            CreditId={CreditId}
            interest={interest}
            totalAmount={formatCurrency(totalAmount)}
            principal={principal}
            handleClose={handlePopUpClose}
            saveChanges={handleSubmit}
            message={translate("chooseLoanForMergingCreditLines")}
          />}

          <Backdrop
            sx={{
              color: "#fff",
              zIndex: 2000,
              display: "flex",
              flexDirection: "column",
            }}
            open={dataLoading}
          >
            <CircularProgress color="inherit" />
            <Typography color={"white"}>Please wait...</Typography>
          </Backdrop>
        </StyledTwoChildContainer>
      </StyledOneChildContainer>
    </StyledParentContainer>
  );
};

export default CreditDetails;
